import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import fileSystemService from '../../../services/FileSystemService';
import firebaseService from '../services/FirebaseService'; // kept for Auth only
import supabaseService from '../../../services/SupabaseService';
import { useAuth } from '../../../contexts/AuthContext';
import UserTierService from '../../../models/UserTier';

const DraftContext = createContext();

export function DraftProvider({ children }) {
  // Get user info from AuthContext
  const { userProfile, currentUser } = useAuth();

  // Info fields
  const [teamName, setTeamName] = useState('');
  const [tournament, setTournament] = useState('');
  const [stage, setStage] = useState('');
  const [draftDate, setDraftDate] = useState(() => {
    const d = new Date();
    return d.toISOString().slice(0, 10);
  });

  // Pick mode state - shared across components
  const [pickMode, setPickMode] = useState('first');

  // Both pick modes
  const [scenariosFirstPick, setScenariosFirstPick] = useState([
    { id: 0, title: 'Scenario 1', picks: Array(24).fill(null), swapSides: false }
  ]);
  const [scenariosSecondPick, setScenariosSecondPick] = useState([
    { id: 0, title: 'Scenario 1', picks: Array(24).fill(null), swapSides: true }
  ]);

  // Top hero-pool row — 15 slots by default, grows when Opp Pool data or a
  // manual expand needs more. Persisted to sessionStorage so hard refresh
  // survives, but a manual expand's trailing EMPTY slots are a session
  // convenience, not something that should surprise the user next load —
  // trim back down to the true minimum (15, or wherever the last actual
  // hero pick is) on every load, keeping only what's actually needed.
  const [foreheadPicks, setForeheadPicks] = useState(() => {
    try {
      const stored = sessionStorage.getItem('fh_picks');
      if (stored) {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed) && parsed.length >= 15) {
          let lastFilled = -1;
          for (let i = parsed.length - 1; i >= 0; i--) {
            if (parsed[i]?.hero) { lastFilled = i; break; }
          }
          const minLen = Math.max(15, lastFilled + 1);
          return parsed.length > minLen ? parsed.slice(0, minLen) : parsed;
        }
      }
    } catch {}
    return Array(15).fill(null);
  });
  // Connector arrows between the forehead slots — persisted to sessionStorage.
  const [foreheadConnectors, setForeheadConnectors] = useState(() => {
    try {
      const stored = sessionStorage.getItem('fh_connectors');
      if (stored) {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed)) return parsed;
      }
    } catch {}
    return [];
  });

  // Sync forehead state to sessionStorage so hard refresh (Cmd+Shift+R) survives.
  useEffect(() => {
    try { sessionStorage.setItem('fh_picks', JSON.stringify(foreheadPicks)); } catch {}
  }, [foreheadPicks]);
  useEffect(() => {
    try { sessionStorage.setItem('fh_connectors', JSON.stringify(foreheadConnectors)); } catch {}
  }, [foreheadConnectors]);

  // Live GSI draft timing data — saved with the draft.
  // Each entry: { seq, boardPos, deltaSeconds, heroName }
  const [pickTimings, setPickTimings] = useState([]);
  // Dota 2 match ID from GSI — used to link ygbuff scrims to this draft.
  const [gsiMatchId, setGsiMatchId] = useState(null);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [currentDraftId, setCurrentDraftId] = useState(null);
  const [downloadedDrafts, setDownloadedDrafts] = useState([]);
  const [isFileSystemApiSupported, setIsFileSystemApiSupported] = useState(false);
  const [selectedFolderPath, setSelectedFolderPath] = useState(null);
  const [originalDraftData, setOriginalDraftData] = useState(null);

  // Undo/Redo state management
  const [history, setHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [isUndoRedoAction, setIsUndoRedoAction] = useState(false);

  // Track pulled match heroes for highlighting
  // Now stores an ARRAY of match data to support multiple match pulls
  const [pulledMatchHeroes, setPulledMatchHeroes] = useState([]);

  // Check if File System Access API is supported
  useEffect(() => {
    setIsFileSystemApiSupported(!!window.showDirectoryPicker);
    // Load the saved folder path
    const savedPath = fileSystemService.getSavedFolderPath();
    if (savedPath) {
      setSelectedFolderPath(savedPath);
    }
    
    // Pre-cache any existing drafts in tracking
    preCacheExistingDrafts();
  }, []);
  
  // Pre-cache existing drafts from tracking
  const preCacheExistingDrafts = () => {
    try {
      const tracking = fileSystemService.getDraftTracking();
      if (!tracking) return;

      // Check if each draft in tracking is already cached
      Object.keys(tracking).forEach(draftId => {
        const cachedDraftJson = localStorage.getItem(`draft_cache_${draftId}`);
        if (!cachedDraftJson) {
          console.log(`Draft ${draftId} not cached yet`);
        }
      });
    } catch (error) {
      console.error('Error pre-caching drafts:', error);
    }
  };

  // Block browser close/refresh when there are unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasUnsavedChanges) {
        // Modern browsers require returnValue to be set
        e.preventDefault();
        e.returnValue = '';
        return '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [hasUnsavedChanges]);

  // Save current state to history (for undo/redo)
  useEffect(() => {
    // Skip if this is an undo/redo action to prevent adding to history
    if (isUndoRedoAction) {
      setIsUndoRedoAction(false);
      return;
    }

    const currentState = {
      scenariosFirstPick: JSON.parse(JSON.stringify(scenariosFirstPick)),
      scenariosSecondPick: JSON.parse(JSON.stringify(scenariosSecondPick)),
      foreheadPicks: JSON.parse(JSON.stringify(foreheadPicks)),
      foreheadConnectors: JSON.parse(JSON.stringify(foreheadConnectors)),
      teamName,
      tournament,
      stage,
      draftDate
    };

    // Add to history, removing any future history if we're not at the end
    setHistory(prev => {
      const newHistory = prev.slice(0, historyIndex + 1);
      newHistory.push(currentState);
      // Limit history to 50 states
      if (newHistory.length > 50) {
        newHistory.shift();
        return newHistory;
      }
      return newHistory;
    });
    setHistoryIndex(prev => {
      const newIndex = Math.min(prev + 1, 49);
      return newIndex;
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scenariosFirstPick, scenariosSecondPick, foreheadPicks, foreheadConnectors, teamName, tournament, stage, draftDate]);

  // Undo function
  const undo = () => {
    if (historyIndex <= 0) return; // Can't undo if at the beginning

    const previousState = history[historyIndex - 1];
    if (!previousState) return;

    setIsUndoRedoAction(true);
    setScenariosFirstPick(JSON.parse(JSON.stringify(previousState.scenariosFirstPick)));
    setScenariosSecondPick(JSON.parse(JSON.stringify(previousState.scenariosSecondPick)));
    if (previousState.foreheadPicks) setForeheadPicks(JSON.parse(JSON.stringify(previousState.foreheadPicks)));
    if (previousState.foreheadConnectors) setForeheadConnectors(JSON.parse(JSON.stringify(previousState.foreheadConnectors)));
    setTeamName(previousState.teamName);
    setTournament(previousState.tournament);
    setStage(previousState.stage);
    setDraftDate(previousState.draftDate);
    setHistoryIndex(prev => prev - 1);
    setHasUnsavedChanges(true);
  };

  // Redo function
  const redo = () => {
    if (historyIndex >= history.length - 1) return; // Can't redo if at the end

    const nextState = history[historyIndex + 1];
    if (!nextState) return;

    setIsUndoRedoAction(true);
    setScenariosFirstPick(JSON.parse(JSON.stringify(nextState.scenariosFirstPick)));
    setScenariosSecondPick(JSON.parse(JSON.stringify(nextState.scenariosSecondPick)));
    if (nextState.foreheadPicks) setForeheadPicks(JSON.parse(JSON.stringify(nextState.foreheadPicks)));
    if (nextState.foreheadConnectors) setForeheadConnectors(JSON.parse(JSON.stringify(nextState.foreheadConnectors)));
    setTeamName(nextState.teamName);
    setTournament(nextState.tournament);
    setStage(nextState.stage);
    setDraftDate(nextState.draftDate);
    setHistoryIndex(prev => prev + 1);
    setHasUnsavedChanges(true);
  };

  // Upsert a live-GSI scenario (created/updated as the python GSI server emits
  // actions). Tagged with `gsiMatchId` so we identify and update the same
  // scenario across polls instead of creating a new one each tick. Always
  // writes to scenariosFirstPick — the board's pick mode is independent.
  const importLiveDraft = useCallback((matchId, picksArray, opts = {}) => {
    if (!matchId) return;
    setGsiMatchId(matchId);
    setScenariosFirstPick(prev => {
      const idx = prev.findIndex(s => s.gsiMatchId === matchId);
      if (idx !== -1) {
        return prev.map((s, i) => i === idx ? { ...s, picks: picksArray } : s);
      }
      const maxId = prev.length > 0 ? Math.max(...prev.map(s => s.id)) : -1;
      const newScenario = {
        id: maxId + 1,
        title: opts.title || `Live ${matchId}`,
        picks: picksArray,
        swapSides: false,
        gsiMatchId: matchId,
        fromMatchPull: true,
      };
      return [...prev, newScenario];
    });
    setHasUnsavedChanges(true);
  }, []);

  // Add scenario for a pick mode
  const addScenario = (pickMode) => {
    const setScenarios = pickMode === 'first' ? setScenariosFirstPick : setScenariosSecondPick;
    const scenarios = pickMode === 'first' ? scenariosFirstPick : scenariosSecondPick;

    // Check tier limits before adding
    const limitCheck = UserTierService.canAddScenario(
      userProfile,
      scenarios.length,
      0 // matchPullCount - will be tracked separately
    );

    if (!limitCheck.canAdd) {
      alert(limitCheck.reason);
      return;
    }

    const maxId = scenarios.length > 0 ? Math.max(...scenarios.map(s => s.id)) : -1;
    const newScenario = {
      id: maxId + 1,
      title: `Scenario ${maxId + 2}`,
      picks: Array(24).fill(null),
      swapSides: pickMode === 'second' // 2nd pick swapped by default, 1st pick not swapped
    };
    setScenarios([...scenarios, newScenario]);
    setHasUnsavedChanges(true);
  };

  // Duplicate scenario for a pick mode
  const duplicateScenario = (pickMode, scenarioId) => {
    const scenarios = pickMode === 'first' ? scenariosFirstPick : scenariosSecondPick;

    // Check tier limits before duplicating
    const canAddResult = UserTierService.canAddScenario(userProfile, scenarios.length);
    if (!canAddResult.canAdd) {
      alert(canAddResult.reason);
      return;
    }

    const setScenarios = pickMode === 'first' ? setScenariosFirstPick : setScenariosSecondPick;
    const scenarioToDuplicate = scenarios.find(s => s.id === scenarioId);
    if (!scenarioToDuplicate) return;

    // Find the index of the scenario to duplicate
    const scenarioIndex = scenarios.findIndex(s => s.id === scenarioId);

    const maxId = Math.max(...scenarios.map(s => s.id));
    const newScenario = {
      ...scenarioToDuplicate, // Copy all properties
      id: maxId + 1,
      title: `${scenarioToDuplicate.title} (Copy)`,
      picks: JSON.parse(JSON.stringify(scenarioToDuplicate.picks)),
      practiceMatchups: scenarioToDuplicate.practiceMatchups ? JSON.parse(JSON.stringify(scenarioToDuplicate.practiceMatchups)) : {}
    };

    // Insert the new scenario immediately after the duplicated one
    const newScenarios = [
      ...scenarios.slice(0, scenarioIndex + 1),
      newScenario,
      ...scenarios.slice(scenarioIndex + 1)
    ];

    setScenarios(newScenarios);
    setHasUnsavedChanges(true);
  };

  // Delete scenario for a pick mode
  const deleteScenario = (pickMode, scenarioId) => {
    const setScenarios = pickMode === 'first' ? setScenariosFirstPick : setScenariosSecondPick;
    const scenarios = pickMode === 'first' ? scenariosFirstPick : scenariosSecondPick;
    if (scenarios.length <= 1) return;
    setScenarios(scenarios.filter(s => s.id !== scenarioId));
    setHasUnsavedChanges(true);
  };

  // Update scenario for a pick mode
  const updateScenario = (pickMode, scenarioId, updates) => {
    const setScenarios = pickMode === 'first' ? setScenariosFirstPick : setScenariosSecondPick;
    // Use functional update to ensure we're working with the latest state
    setScenarios(prevScenarios => prevScenarios.map(s => s.id === scenarioId ? { ...s, ...updates } : s));
    setHasUnsavedChanges(true);
  };

  // Reorder scenarios for a pick mode
  const reorderScenarios = (pickMode, fromIndex, toIndex) => {
    const setScenarios = pickMode === 'first' ? setScenariosFirstPick : setScenariosSecondPick;
    setScenarios(prevScenarios => {
      const newScenarios = [...prevScenarios];
      const [movedScenario] = newScenarios.splice(fromIndex, 1);
      newScenarios.splice(toIndex, 0, movedScenario);
      return newScenarios;
    });
    setHasUnsavedChanges(true);
  };

  // Function to detect if current draft has changes compared to original
  const hasSignificantChanges = () => {
    if (!originalDraftData || !currentDraftId) return false;
    
    const currentData = {
      scenariosFirstPick,
      scenariosSecondPick,
      teamName,
      tournament,
      stage,
      date: draftDate
    };
    
    // Check for changes in basic fields
    if (originalDraftData.teamName !== currentData.teamName ||
        originalDraftData.tournament !== currentData.tournament ||
        originalDraftData.stage !== currentData.stage ||
        originalDraftData.date !== currentData.date) {
      return true;
    }
    
    // Check for changes in scenarios (deep comparison)
    const originalFirstPick = JSON.stringify(originalDraftData.scenariosFirstPick || []);
    const currentFirstPick = JSON.stringify(currentData.scenariosFirstPick || []);
    const originalSecondPick = JSON.stringify(originalDraftData.scenariosSecondPick || []);
    const currentSecondPick = JSON.stringify(currentData.scenariosSecondPick || []);
    
    if (originalFirstPick !== currentFirstPick || originalSecondPick !== currentSecondPick) {
      return true;
    }
    
    return false;
  };

  // Save draft (both pick modes and info fields)
  const saveDraft = async (draftInfo = {}) => {
    console.log('='.repeat(80));
    console.log('💾 SAVE DRAFT: Starting save process...');
    console.log('💾 User Profile:', userProfile);
    console.log('💾 Team Name:', teamName);
    console.log('💾 Tournament:', tournament);
    console.log('💾 Stage:', stage);
    console.log('💾 Date:', draftDate);
    console.log('💾 Scenarios First Pick:', scenariosFirstPick?.length || 0);
    console.log('💾 Scenarios Second Pick:', scenariosSecondPick?.length || 0);
    console.log('='.repeat(80));

    setLoading(true);
    setError(null);
    try {
      // If we're editing an existing draft and there are significant changes, create a new draft
      const shouldCreateNew = currentDraftId && hasSignificantChanges();

      const draftData = {
        id: shouldCreateNew ? null : currentDraftId, // null ID forces creation of new draft
        scenariosFirstPick,
        scenariosSecondPick,
        foreheadPicks,
        foreheadConnectors,
        pickTimings,
        gsiMatchId,
        teamName,
        tournament,
        stage,
        title: draftInfo.title || `${teamName || 'Untitled'} - ${tournament || 'Draft'}`,
        date: draftDate,
        layoutVersion: '4.0', // Patch 4.0 layout - new side-by-side positioning
        ...draftInfo
      };

      console.log('💾 Draft data prepared:', draftData);

      // Save to local file system first (for all users)
      console.log('💾 Saving to local file system...');
      const savedId = await fileSystemService.saveDraft(draftData);
      console.log('💾 Saved to local file system with ID:', savedId);

      // Check if user has premium access
      const hasViewRecordsAccess = UserTierService.hasFeatureAccess(userProfile, 'canSeeViewRecords');
      console.log('💾 Has View Records access (Premium)?', hasViewRecordsAccess);

      // TEMPORARY: Also check if user is logged in via Firebase
      const currentUser = firebaseService.getCurrentUser();
      console.log('💾 Firebase current user:', currentUser);
      console.log('💾 Firebase current user UID:', currentUser?.uid);
      console.log('💾 Firebase current user email:', currentUser?.email);
      const isLoggedIn = !!currentUser;
      console.log('💾 Is user logged in?', isLoggedIn);

      // Save to Supabase (primary) + Firestore (fallback) for logged-in users
      let cloudSaved = false;
      let cloudError = null;
      if (isLoggedIn) {
        console.log('💾 ✅ User is logged in - will save to Supabase');
        draftData.id = savedId;
        draftData.userId = currentUser.uid;
        draftData.userEmail = currentUser.email;

        // Dual-write: save to BOTH Supabase and Firestore for resilience
        let supabaseOk = false;
        try {
          const supabaseId = await supabaseService.saveDraft(draftData);
          console.log('✅ Draft saved to Supabase successfully:', supabaseId);
          supabaseOk = true;
          cloudSaved = true;
        } catch (supabaseError) {
          cloudError = supabaseError?.message || String(supabaseError);
          console.warn('⚠️ Supabase save failed:', cloudError, supabaseError);
        }

        try {
          await firebaseService.saveDraft({ ...draftData });
          console.log('✅ Draft saved to Firestore successfully');
          cloudSaved = true;
          cloudError = null;
        } catch (firestoreError) {
          if (!supabaseOk) {
            cloudError = cloudError || firestoreError?.message || String(firestoreError);
            console.error('❌ Both Supabase and Firestore failed:', firestoreError);
          } else {
            console.warn('⚠️ Firestore save failed (Supabase succeeded):', firestoreError);
          }
        }
      } else {
        console.log('⚠️ Skipping cloud save - user NOT logged in');
      }

      setCurrentDraftId(savedId);
      setHasUnsavedChanges(false);

      // Update original draft data to current state if we created a new draft
      if (shouldCreateNew) {
        setOriginalDraftData({
          scenariosFirstPick,
          scenariosSecondPick,
          teamName,
          tournament,
          stage,
          date: draftDate
        });
        console.log('Created new draft due to changes. New ID:', savedId);
      }

      setLoading(false);
      console.log('💾 SAVE DRAFT: Complete!');
      return { id: savedId, cloudSaved, loggedIn: isLoggedIn, cloudError };
    } catch (error) {
      console.error('❌ SAVE DRAFT ERROR:', error);
      setError(error.message);
      setLoading(false);
      throw error;
    }
  };

  // Load draft (both pick modes and info fields)
  const loadDraft = async (draftId, filename, skipConfirmation = false) => {
    // Check for unsaved changes and show confirmation if needed
    if (!skipConfirmation && (hasUnsavedChanges || hasSignificantChanges())) {
      return { needsConfirmation: true, draftId, filename };
    }

    setLoading(true);
    setError(null);
    try {
      console.log(`Loading draft: ${draftId}, filename: ${filename}`);

      // Try to get draft data from cache first
      const cachedDraftJson = localStorage.getItem(`draft_cache_${draftId}`);
      if (cachedDraftJson) {
        try {
          const cachedDraft = JSON.parse(cachedDraftJson);
          console.log('Found cached draft data:', draftId);
          loadDraftData(cachedDraft);
          setLoading(false);
          return cachedDraft;
        } catch (cacheError) {
          console.warn('Could not load cached draft, falling back to file', cacheError);
        }
      }

      // Try to load from file system service
      let draftData = null;
      try {
        draftData = await fileSystemService.getDraft(draftId, filename);
      } catch (fsError) {
        console.warn('Could not load from file system, trying Supabase:', fsError);
      }

      // If file system failed, try Supabase, then Firestore
      if (!draftData) {
        try {
          draftData = await supabaseService.getDraft(draftId);
          console.log('Loaded draft from Supabase:', draftId);
        } catch (supabaseError) {
          console.warn('Could not load from Supabase:', supabaseError);
        }
      }

      if (!draftData) {
        try {
          draftData = await firebaseService.getDraft(draftId);
          console.log('Loaded draft from Firestore fallback:', draftId);
        } catch (firestoreError) {
          console.error('Could not load from Firestore either:', firestoreError);
        }
      }

      if (!draftData) throw new Error('Draft not found in tracking. Please import it again.');

      // Load draft data
      loadDraftData(draftData);

      // Cache the draft data for future quick loading
      try {
        localStorage.setItem(`draft_cache_${draftId}`, JSON.stringify(draftData));
        console.log('Draft cached from load:', draftId);
      } catch (cacheError) {
        console.warn('Could not cache draft data', cacheError);
      }

      setLoading(false);
      return draftData;
    } catch (error) {
      setError(error.message);
      setLoading(false);
      throw error;
    }
  };

  // Helper function to load draft data into state
  const loadDraftData = (draftData) => {
    // Treat empty arrays as missing — Supabase returns [] when a pick mode has no scenarios,
    // which would leave that tab with no draftboard. Fall back to a default scenario instead.
    const firstPick = (Array.isArray(draftData.scenariosFirstPick) && draftData.scenariosFirstPick.length > 0)
      ? draftData.scenariosFirstPick
      : [{ id: 0, title: 'Scenario 1', picks: Array(24).fill(null), swapSides: false }];
    const secondPick = (Array.isArray(draftData.scenariosSecondPick) && draftData.scenariosSecondPick.length > 0)
      ? draftData.scenariosSecondPick
      : [{ id: 0, title: 'Scenario 1', picks: Array(24).fill(null), swapSides: true }];
    const teamName = draftData.teamName || '';
    const tournament = draftData.tournament || '';
    const stage = draftData.stage || '';
    const date = draftData.date || (new Date().toISOString().slice(0, 10));

    const fh = Array.isArray(draftData.foreheadPicks) && draftData.foreheadPicks.length >= 15
      ? draftData.foreheadPicks
      : Array(15).fill(null);

    const fc = Array.isArray(draftData.foreheadConnectors)
      ? draftData.foreheadConnectors
      : [];

    const pt = Array.isArray(draftData.pickTimings) ? draftData.pickTimings : [];
    const gmi = draftData.gsiMatchId || null;

    setScenariosFirstPick(firstPick);
    setScenariosSecondPick(secondPick);
    setForeheadPicks(fh);
    setForeheadConnectors(fc);
    setPickTimings(pt);
    setGsiMatchId(gmi);
    setTeamName(teamName);
    setTournament(tournament);
    setStage(stage);
    setDraftDate(date);
    setCurrentDraftId(draftData.id);
    setHasUnsavedChanges(false);

    // Rebuild pulledMatchHeroes from scenarios that have fromMatchPull: true
    // This ensures highlights work correctly after loading saved colored boards
    const rebuiltMatchHeroes = [];
    const allScenarios = [...firstPick, ...secondPick];

    allScenarios.forEach(scenario => {
      if (scenario.fromMatchPull && scenario.picks) {
        const matchId = scenario.matchId || 'unknown';
        scenario.picks.forEach((pick, index) => {
          if (pick && pick.hero && pick.hero.id) {
            rebuiltMatchHeroes.push({
              heroId: pick.hero.id,
              position: index + 1, // 1-24
              matchId: matchId
            });
          }
        });
      }
    });

    if (rebuiltMatchHeroes.length > 0) {
      console.log('🔄 Rebuilt pulledMatchHeroes from saved draft:', rebuiltMatchHeroes.length, 'heroes');
      setPulledMatchHeroes(rebuiltMatchHeroes);
    } else {
      // Clear pulledMatchHeroes if no match pulls in loaded draft
      setPulledMatchHeroes([]);
    }

    // Store original draft data for change detection
    setOriginalDraftData({
      scenariosFirstPick: JSON.parse(JSON.stringify(firstPick)), // Deep copy
      scenariosSecondPick: JSON.parse(JSON.stringify(secondPick)), // Deep copy
      teamName,
      tournament,
      stage,
      date
    });
  };

  // Merge imported draft data with current draft
  const mergeDraftData = (importedData) => {
    // Get the imported scenarios
    const importedFirstPick = importedData.scenariosFirstPick || [];
    const importedSecondPick = importedData.scenariosSecondPick || [];

    // Get current max IDs to avoid conflicts
    const maxFirstPickId = scenariosFirstPick.length > 0
      ? Math.max(...scenariosFirstPick.map(s => s.id))
      : -1;
    const maxSecondPickId = scenariosSecondPick.length > 0
      ? Math.max(...scenariosSecondPick.map(s => s.id))
      : -1;

    // Renumber imported scenarios to avoid ID conflicts
    const renumberedFirstPick = importedFirstPick.map((scenario, index) => ({
      ...scenario,
      id: maxFirstPickId + 1 + index,
      title: scenario.title || `Imported Scenario ${maxFirstPickId + 2 + index}`
    }));

    const renumberedSecondPick = importedSecondPick.map((scenario, index) => ({
      ...scenario,
      id: maxSecondPickId + 1 + index,
      title: scenario.title || `Imported Scenario ${maxSecondPickId + 2 + index}`
    }));

    // Merge scenarios - add imported scenarios to existing ones
    setScenariosFirstPick(prev => [...prev, ...renumberedFirstPick]);
    setScenariosSecondPick(prev => [...prev, ...renumberedSecondPick]);

    // Copy forehead picks/connectors if the imported file has them
    if (Array.isArray(importedData.foreheadPicks) && importedData.foreheadPicks.length >= 15) {
      setForeheadPicks(JSON.parse(JSON.stringify(importedData.foreheadPicks)));
    }
    if (Array.isArray(importedData.foreheadConnectors)) {
      setForeheadConnectors(JSON.parse(JSON.stringify(importedData.foreheadConnectors)));
    }

    // Keep current draft info (don't overwrite with imported metadata)
    // Mark as having unsaved changes since we merged new content
    setHasUnsavedChanges(true);

    console.log('Merged drafts:', {
      addedFirstPick: renumberedFirstPick.length,
      addedSecondPick: renumberedSecondPick.length,
      totalFirstPick: scenariosFirstPick.length + renumberedFirstPick.length,
      totalSecondPick: scenariosSecondPick.length + renumberedSecondPick.length
    });
  };

  // Load draft directly from file handle
  const loadDraftFromHandle = async (fileHandle) => {
    setLoading(true);
    setError(null);
    try {
      const draftData = await fileSystemService.loadDraftFromHandle(fileHandle);
      if (!draftData) throw new Error('Draft not found');
      
      // Load draft data
      loadDraftData(draftData);
      
      setLoading(false);
      return draftData;
    } catch (error) {
      setError(error.message);
      setLoading(false);
      throw error;
    }
  };

  // Scan for drafts in the selected folder
  const scanDownloadsForDrafts = async () => {
    setLoading(true);
    setError(null);
    try {
      const drafts = await fileSystemService.scanDownloadsForDrafts();
      setDownloadedDrafts(drafts);
      
      // Update the selected folder path
      const savedPath = fileSystemService.getSavedFolderPath();
      if (savedPath) {
        setSelectedFolderPath(savedPath);
      }
      
      setLoading(false);
      return drafts;
    } catch (error) {
      setError(error.message);
      setLoading(false);
      throw error;
    }
  };

  // Try to scan the default Downloads folder
  const scanDefaultDownloadsFolder = async () => {
    setLoading(true);
    setError(null);
    try {
      const drafts = await fileSystemService.scanDefaultDownloadsFolder();
      setDownloadedDrafts(drafts);
      
      // Update the selected folder path
      const savedPath = fileSystemService.getSavedFolderPath();
      if (savedPath) {
        setSelectedFolderPath(savedPath);
      }
      
      setLoading(false);
      return drafts;
    } catch (error) {
      setError(error.message);
      setLoading(false);
      throw error;
    }
  };

  // Import a draft from file
  const importDraft = async (skipConfirmation = false) => {
    // Check for unsaved changes and show confirmation if needed
    if (!skipConfirmation && (hasUnsavedChanges || hasSignificantChanges())) {
      return { needsConfirmation: true };
    }

    setLoading(true);
    setError(null);
    try {
      const draftData = await fileSystemService.loadDraftFile();
      if (!draftData) throw new Error('Failed to import draft');
      
      // Load draft data
      loadDraftData(draftData);
      
      setLoading(false);
      return draftData;
    } catch (error) {
      setError(error.message);
      setLoading(false);
      throw error;
    }
  };

  // Import draft data directly (for drag-and-drop)
  const importDraftData = async (draftData, skipConfirmation = false) => {
    // Check for unsaved changes and show confirmation if needed
    if (!skipConfirmation && (hasUnsavedChanges || hasSignificantChanges())) {
      return new Promise((resolve, reject) => {
        // Return a promise that will be resolved by the confirmation dialog
        resolve({ needsConfirmation: true, draftData });
      });
    }

    setLoading(true);
    setError(null);
    try {
      if (!draftData) throw new Error('No draft data provided');
      
      // Load draft data
      loadDraftData(draftData);
      
      setLoading(false);
      return draftData;
    } catch (error) {
      setError(error.message);
      setLoading(false);
      throw error;
    }
  };

  // Get all drafts
  const getDrafts = async () => {
    setLoading(true);
    setError(null);
    try {
      const drafts = fileSystemService.getDrafts();
      setLoading(false);
      return drafts;
    } catch (error) {
      setError(error.message);
      setLoading(false);
      throw error;
    }
  };

  // Create a new draft (reset to initial state)
  const newDraft = (skipConfirmation = false) => {
    // Check for unsaved changes and show confirmation if needed
    if (!skipConfirmation && (hasUnsavedChanges || hasSignificantChanges())) {
      return { needsConfirmation: true };
    }

    // Reset to initial state
    setScenariosFirstPick([{ id: 0, title: 'Scenario 1', picks: Array(24).fill(null) }]);
    setScenariosSecondPick([{ id: 0, title: 'Scenario 1', picks: Array(24).fill(null) }]);
    setForeheadPicks(Array(15).fill(null));
    setForeheadConnectors([]);
    try { sessionStorage.removeItem('fh_picks'); sessionStorage.removeItem('fh_connectors'); } catch {}
    setPickTimings([]);
    setGsiMatchId(null);
    setTeamName('');
    setTournament('');
    setStage('');
    setDraftDate(new Date().toISOString().slice(0, 10));
    setCurrentDraftId(null);
    setHasUnsavedChanges(false);
    setOriginalDraftData(null); // Clear original data when creating new draft
    setPulledMatchHeroes([]); // Clear pulled match highlights
    return { success: true };
  };

  // Delete a draft
  const deleteDraft = async (draftId) => {
    setLoading(true);
    setError(null);
    try {
      // Delete from both Supabase and Firestore
      if (currentUser && currentUser.uid) {
        try {
          await supabaseService.deleteDraft(draftId, currentUser.uid);
          console.log('Draft deleted from Supabase:', draftId);
        } catch (supabaseError) {
          console.warn('Could not delete from Supabase:', supabaseError);
        }
        try {
          await firebaseService.deleteDraft(draftId);
          console.log('Draft deleted from Firestore:', draftId);
        } catch (firestoreError) {
          console.warn('Could not delete from Firestore:', firestoreError);
        }
      }

      // Delete from local storage
      const success = fileSystemService.deleteDraft(draftId);

      // Clear cache
      localStorage.removeItem(`draft_cache_${draftId}`);

      setLoading(false);
      if (currentDraftId === draftId) {
        // Reset to initial state
        setScenariosFirstPick([{ id: 0, title: 'Scenario 1', picks: Array(24).fill(null) }]);
        setScenariosSecondPick([{ id: 0, title: 'Scenario 1', picks: Array(24).fill(null) }]);
        setTeamName('');
        setTournament('');
        setStage('');
        setDraftDate(new Date().toISOString().slice(0, 10));
        setCurrentDraftId(null);
        setHasUnsavedChanges(false);
      }
      return success;
    } catch (error) {
      setError(error.message);
      setLoading(false);
      throw error;
    }
  };

  const value = {
    scenariosFirstPick,
    scenariosSecondPick,
    pickMode,
    setPickMode,
    addScenario,
    importLiveDraft,
    duplicateScenario,
    deleteScenario,
    updateScenario,
    reorderScenarios,
    saveDraft,
    loadDraft,
    loadDraftFromHandle,
    importDraft,
    importDraftData,
    mergeDraftData,
    getDrafts,
    scanDownloadsForDrafts,
    scanDefaultDownloadsFolder,
    downloadedDrafts,
    isFileSystemApiSupported,
    newDraft,
    deleteDraft,
    loadDraftData,
    foreheadPicks, setForeheadPicks,
    foreheadConnectors, setForeheadConnectors,
    pickTimings, setPickTimings,
    gsiMatchId,
    teamName, setTeamName,
    tournament, setTournament,
    stage, setStage,
    draftDate, setDraftDate,
    hasUnsavedChanges,
    hasSignificantChanges,
    currentDraftId,
    loading,
    error,
    selectedFolderPath,
    undo,
    redo,
    canUndo: historyIndex > 0,
    canRedo: historyIndex < history.length - 1,
    pulledMatchHeroes,
    setPulledMatchHeroes,
    userProfile // Expose user tier info to components
  };

  return (
    <DraftContext.Provider value={value}>
      {children}
    </DraftContext.Provider>
  );
}

export function useDraft() {
  return useContext(DraftContext);
}
